import React, { useState } from 'react';
import { Form, Input, Button, Card, Tabs, message } from 'antd';
import { UserOutlined, LockOutlined, GiftOutlined } from '@ant-design/icons';
import { authAPI } from '../services/api';
import { setToken, setUser } from '../utils/auth';
import './LoginPage.css';

interface LoginPageProps {
  onLogin: (token: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      const response = await authAPI.login(values);
      const { token, user } = response.data;
      setToken(token);
      setUser(user);
      onLogin(token);
    } catch (error: any) {
      console.error('Login failed:', error);
      if (error.response?.status === 401) {
        message.error('用户名或密码错误！');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message === 'Network Error') {
        message.error('网络连接失败，请检查网络设置');
      } else {
        message.error('登录失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: any) => {
    setLoading(true);
    try {
      const response = await authAPI.register(values);
      const { token, user } = response.data;
      setToken(token);
      setUser(user);
      message.success('注册成功！');
      onLogin(token);
    } catch (error: any) {
      console.error('Register failed:', error);
      if (error.response?.status === 409) {
        message.error('用户名已存在！');
      } else if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message === 'Network Error') {
        message.error('网络连接失败，请检查网络设置');
      } else {
        message.error('注册失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-scene" aria-hidden="true">
        <div className="login-note login-note-main" />
        <div className="login-note login-note-small" />
        <div className="login-pencil" />
      </div>
      <div className="login-container">
        <Card className="login-card">
          <h1 className="login-title">英语作文批改系统</h1>
          <p className="login-subtitle">每篇作文都能看清下一步怎么改</p>
          <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
            <Tabs.TabPane tab="登录" key="login">
              <Form
                name="login"
                onFinish={handleLogin}
                autoComplete="off"
                size="large"
              >
                <Form.Item
                  name="username"
                  rules={[{ required: true, message: '请输入用户名!' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: '请输入密码!' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </Tabs.TabPane>

            <Tabs.TabPane tab="注册" key="register">
              <Form
                name="register"
                onFinish={handleRegister}
                autoComplete="off"
                size="large"
              >
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: '请输入用户名!' },
                    { min: 3, message: '用户名至少3个字符!' },
                    { max: 20, message: '用户名最多20个字符!' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码!' },
                    { min: 6, message: '密码至少6个字符!' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: '请确认密码!' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致!'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="确认密码"
                  />
                </Form.Item>

                <Form.Item
                  name="name"
                  rules={[{ required: true, message: '请输入姓名!' }]}
                >
                  <Input placeholder="姓名" />
                </Form.Item>

                <Form.Item
                  name="invitation_code"
                  rules={[
                    { required: true, message: '请输入邀请码!' },
                    { min: 8, message: '邀请码格式不正确!' },
                  ]}
                >
                  <Input
                    prefix={<GiftOutlined />}
                    placeholder="邀请码"
                    maxLength={12}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    注册
                  </Button>
                </Form.Item>
              </Form>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
